import { useEffect, useState } from "react";
import { masterFetchAbs } from "./masterApi";
import MasterLayout from "./MasterLayout";

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let x = n;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(1)} ${u[i]}`;
}

function fmtAge(sec) {
  if (sec == null) return "never";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function healthColor(g) {
  if (!g.last_seen) return "#5b5170"; // grey — never seen
  const age = g.seconds_since_ping ?? 999999;
  if (age < 180) return "#22c55e";   // green — <3 min
  if (age < 600) return "#f59e0b";   // amber — <10 min
  return "#ef4444";                   // red — stale
}

export default function MasterImageGuardians() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    masterFetchAbs("/api/mirror/stats")
      .then((d) => { setRows(d.guardians || []); setError(""); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <MasterLayout>
    <div data-testid="master-image-guardians">
      <div style={{ fontSize: 11, letterSpacing: 3, opacity: 0.5 }}>PLATFORM</div>
      <h1 style={{ fontSize: 26, margin: "4px 0 4px" }}>Image Guardians</h1>
      <p style={{ opacity: 0.6, marginTop: 0, marginBottom: 26, fontSize: 13 }}>
        Every site VPS runs a bot that mirrors profile images locally, so images stay online
        even if this platform is down. Green = pinged &lt; 3 min, amber &lt; 10 min, red = stale.
      </p>

      {error && (
        <div data-testid="guardians-error" style={{
          padding: 12, background: "rgba(239,68,68,0.14)",
          border: "1px solid rgba(239,68,68,0.5)", borderRadius: 8,
          marginBottom: 16, fontSize: 13, color: "#fca5a5",
        }}>
          Failed to load: {error}
        </div>
      )}

      {loading && !rows.length ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div data-testid="guardians-empty" style={{
          padding: 24, background: "#110720", border: "1px solid #1f1232",
          borderRadius: 12, opacity: 0.7, fontSize: 14,
        }}>
          No guardians registered yet. Install the bot on a site VPS with:<br />
          <code style={{ background: "#000", padding: "2px 6px", borderRadius: 4 }}>
            sudo bash /opt/image-guardian/install-image-guardian.sh &lt;platform-url&gt; &lt;site-id&gt; &lt;site-api-key&gt;
          </code>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {rows.map((g) => (
            <div
              key={g.site_id}
              data-testid={`guardian-row-${g.site_id}`}
              style={{
                display: "grid",
                gridTemplateColumns: "16px 1.4fr 1fr 1fr 1fr 1fr",
                gap: 14,
                alignItems: "center",
                padding: "14px 16px",
                background: "#110720",
                border: "1px solid #1f1232",
                borderRadius: 10,
                fontSize: 13,
              }}
            >
              <span
                title={g.healthy ? "healthy" : "stale"}
                style={{
                  width: 12, height: 12, borderRadius: "50%",
                  background: healthColor(g),
                  boxShadow: `0 0 8px ${healthColor(g)}`,
                }}
              />
              <div>
                <div style={{ color: "#fff", fontWeight: 600 }}>{g.site_domain || g.site_id}</div>
                <div style={{ opacity: 0.5, fontSize: 11 }}>{g.host || "—"} · v{g.version || "?"}</div>
              </div>
              <div>
                <div style={{ opacity: 0.5, fontSize: 11 }}>Mirrored</div>
                <div>{(g.mirrored_count || 0).toLocaleString()}</div>
              </div>
              <div>
                <div style={{ opacity: 0.5, fontSize: 11 }}>Disk used</div>
                <div>{fmtBytes(g.disk_used_bytes || 0)}</div>
              </div>
              <div>
                <div style={{ opacity: 0.5, fontSize: 11 }}>Last ping</div>
                <div>{fmtAge(g.seconds_since_ping)}</div>
              </div>
              <div>
                <div style={{ opacity: 0.5, fontSize: 11 }}>Last full sync</div>
                <div style={{ fontSize: 11 }}>
                  {g.last_full_sync ? new Date(g.last_full_sync).toLocaleString() : "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </MasterLayout>
  );
}
